import { useState, useEffect, useCallback } from 'react'
import { appsyncQuery } from '../api/appsync-client.js'
import { useSubscription } from '../api/useSubscription.js'

const LIST_SESSIONS = `
  query ListSessions {
    listSessions {
      sessionId userId managerId status energy scheduledAt
      createdAt updatedAt layout sections agenda taskSnapshot
    }
  }
`

const CREATE_SESSION = `
  mutation CreateSession($input: CreateSessionInput) {
    createSession(input: $input) {
      sessionId userId managerId status energy scheduledAt
      createdAt updatedAt layout sections agenda
    }
  }
`

const UPDATE_SESSION = `
  mutation UpdateSession($sessionId: ID!, $input: UpdateSessionInput!) {
    updateSession(sessionId: $sessionId, input: $input) {
      sessionId userId managerId status energy scheduledAt
      createdAt updatedAt layout sections agenda privateNotes
    }
  }
`

const DELETE_SESSION = `
  mutation DeleteSession($sessionId: ID!) {
    deleteSession(sessionId: $sessionId)
  }
`

const READY_SESSION = `
  mutation ReadySession($sessionId: ID!) {
    readySession(sessionId: $sessionId) {
      sessionId userId status energy scheduledAt readyAt
      createdAt updatedAt layout sections agenda
    }
  }
`

const CANCEL_SESSION = `
  mutation CancelSession($sessionId: ID!, $ownerUserId: ID) {
    cancelSession(sessionId: $sessionId, ownerUserId: $ownerUserId) {
      sessionId userId status energy scheduledAt cancelledAt
      createdAt updatedAt layout sections agenda goalSnapshot taskSnapshot
    }
  }
`

const COMPLETE_SESSION = `
  mutation CompleteSession($sessionId: ID!, $ownerUserId: ID) {
    completeSession(sessionId: $sessionId, ownerUserId: $ownerUserId) {
      sessionId userId managerId status energy scheduledAt
      completedAt createdAt updatedAt layout sections agenda goalSnapshot taskSnapshot
    }
  }
`

const ON_SESSION_LIST_CHANGED = `
  subscription OnSessionListChanged($userId: ID!) {
    onSessionListChanged(userId: $userId) {
      sessionId userId managerId status energy scheduledAt
      createdAt updatedAt layout sections agenda taskSnapshot
    }
  }
`

function parseSession(s) {
  if (!s) return s
  if (typeof s.sections === 'string') try { s.sections = JSON.parse(s.sections) } catch { /* keep as-is */ }
  if (typeof s.agenda === 'string') try { s.agenda = JSON.parse(s.agenda) } catch { /* keep as-is */ }
  if (typeof s.goalSnapshot === 'string') try { s.goalSnapshot = JSON.parse(s.goalSnapshot) } catch { /* keep as-is */ }
  if (typeof s.taskSnapshot === 'string') try { s.taskSnapshot = JSON.parse(s.taskSnapshot) } catch { /* keep as-is */ }
  return s
}

export function useSessions({ enabled = true, userId = null } = {}) {
  const [sessions, setSessions] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await appsyncQuery(LIST_SESSIONS)
      setSessions(data.listSessions.map(parseSession))
    } catch (err) {
      setError(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { if (enabled) load() }, [load, enabled])

  // Refetch when tab regains focus
  useEffect(() => {
    if (!enabled) return
    const handler = () => { if (document.visibilityState === 'visible') load() }
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [enabled, load])

  // Subscribe to session list changes for this user (create, status changes)
  useSubscription(
    ON_SESSION_LIST_CHANGED,
    { userId },
    (data) => {
      const updated = parseSession(data.onSessionListChanged)
      if (!updated) return
      setSessions((prev) => {
        const exists = prev.some((s) => s.sessionId === updated.sessionId)
        if (exists) return prev.map((s) => (s.sessionId === updated.sessionId ? updated : s))
        return [updated, ...prev]
      })
    },
    { enabled: enabled && !!userId },
  )

  const createSession = useCallback(async ({ managerId, energy, sections } = {}) => {
    const input = { energy: energy ?? 3, sections: JSON.stringify(sections ?? {}) }
    if (managerId) input.managerId = managerId
    const data = await appsyncQuery(CREATE_SESSION, { input })
    const session = parseSession(data.createSession)
    setSessions((prev) => [session, ...prev])
    return session
  }, [])

  const updateSession = useCallback(async (id, patch) => {
    const input = {}
    if (patch.energy      !== undefined) input.energy      = patch.energy
    if (patch.scheduledAt !== undefined) input.scheduledAt = patch.scheduledAt
    if (patch.layout      !== undefined) input.layout      = patch.layout
    if (patch.privateNotes !== undefined) input.privateNotes = patch.privateNotes
    if (patch.sections    !== undefined) input.sections    = JSON.stringify(patch.sections)
    if (patch.agenda      !== undefined) input.agenda      = JSON.stringify(patch.agenda)
    const data = await appsyncQuery(UPDATE_SESSION, { sessionId: id, input })
    const updated = parseSession(data.updateSession)
    setSessions((prev) => prev.map((s) => (s.sessionId === id ? updated : s)))
    return updated
  }, [])

  const deleteSession = useCallback(async (id) => {
    await appsyncQuery(DELETE_SESSION, { sessionId: id })
    setSessions((prev) => prev.filter((s) => s.sessionId !== id))
  }, [])

  const readySession = useCallback(async (id) => {
    const data = await appsyncQuery(READY_SESSION, { sessionId: id })
    const updated = parseSession(data.readySession)
    setSessions((prev) => prev.map((s) => (s.sessionId === id ? updated : s)))
    return updated
  }, [])

  const cancelSession = useCallback(async (id, ownerUserId = null) => {
    const data = await appsyncQuery(CANCEL_SESSION, { sessionId: id, ownerUserId })
    const updated = parseSession(data.cancelSession)
    setSessions((prev) => prev.map((s) => (s.sessionId === id ? updated : s)))
    return updated
  }, [])

  const completeSession = useCallback(async (id, ownerUserId = null) => {
    const data = await appsyncQuery(COMPLETE_SESSION, { sessionId: id, ownerUserId })
    const updated = parseSession(data.completeSession)
    setSessions((prev) => prev.map((s) => (s.sessionId === id ? updated : s)))
    return updated
  }, [])

  // Merge external data (e.g. from agenda save) into the sessions array
  // without triggering a mutation.
  const patchSession = useCallback((id, patch) => {
    setSessions((prev) => prev.map((s) => (s.sessionId === id ? { ...s, ...patch } : s)))
  }, [])

  return { sessions, loading, error, createSession, updateSession, deleteSession, readySession, cancelSession, completeSession, patchSession, reload: load }
}
